<template>
  <EllipsisMenu
    :id="id"
    :menu-items="menuItems"
    :disabled="disabled"
    class="ship-order-menu"
  />
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import type { PropType, Ref } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  type RygenMenuItem as RygenMenuItemModel,
  Severity,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import {
  AddShipOrderToShipmentPayload,
  CreateShipmentFromShipOrdersPayload,
  PlanningStatus,
  ProposedShipment,
  RemoveFromPlanningPoolPayload,
  RemoveShipOrderFromShipmentPayload,
  SplitShipOrderPayload,
  ValidShipOrder,
} from '@/model/routeBuilder';
import { RoutePlanProposedShipment } from '@/model/routeBuilder/phase2';

import EllipsisMenu from '@rygen/rygen-ui/components/ui/EllipsisMenu.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  shipOrder: {
    type: ValidShipOrder,
    required: true,
  },
  proposedShipments: {
    type: Array as PropType<ProposedShipment[] | RoutePlanProposedShipment[]>,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { id, shipOrder, proposedShipments, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'add-to-shipment', payload: AddShipOrderToShipmentPayload): void;
  (
    event: 'create-shipment',
    payload: CreateShipmentFromShipOrdersPayload,
  ): void;
  (
    event: 'remove-from-planning-pool',
    payload: RemoveFromPlanningPoolPayload,
  ): void;
  (
    event: 'remove-from-shipment',
    payload: RemoveShipOrderFromShipmentPayload,
  ): void;
  (event: 'split', payload: SplitShipOrderPayload): void;
}>();

const { t } = useI18n();

const containingShipments = computed(() => {
  return proposedShipments.value.filter((s) => s.hasShipOrder(shipOrder.value));
});

const addToShipmentCommands: Ref<RygenMenuItemModel[]> = computed(() => {
  if (shipOrder.value.planningStatus === PlanningStatus.PLANNED) {
    return [];
  }

  return proposedShipments.value
    .filter((s) => !s.hasShipOrder(shipOrder.value))
    .map((s) => ({
      label: t('common.paramNum', {
        param: t('shipments.shipments'),
        num: s.identifier,
      }),
      icon: PrimeIcons.PLUS_CIRCLE,
      command: () =>
        emit('add-to-shipment', {
          shipOrder: shipOrder.value,
          shipmentIdentifier: s.identifier,
        }),
    }));
});

const removeFromShipmentCommands: Ref<RygenMenuItemModel[]> = computed(() => {
  return containingShipments.value.length
    ? containingShipments.value.map((s) => ({
        label: t('common.paramNum', {
          param: t('shipments.shipments'),
          num: s.identifier,
        }),
        icon: PrimeIcons.MINUS_CIRCLE,
        severity: Severity.DANGER,
        command: () =>
          emit('remove-from-shipment', {
            shipOrder: shipOrder.value,
            shipmentIdentifiers: [s.identifier],
          }),
      }))
    : [];
});

const menuItems: Ref<RygenMenuItemModel[]> = computed(() => {
  return [
    {
      label: t('common.viewParam', { param: t('orders.orders') }),
      icon: PrimeIcons.EYE,
      to: { name: 'orderDetail', params: { orderId: shipOrder.value.orderId } },
    },
    {
      label: t('common.createParam', { param: t('shipments.shipments') }),
      icon: PrimeIcons.PLUS,
      visible: shipOrder.value.planningStatus !== PlanningStatus.PLANNED,
      command: () => emit('create-shipment', { shipOrders: [shipOrder.value] }),
    },
    {
      label: t('common.addToParam', {
        param: t('shipments.shipments'),
      }),
      icon: PrimeIcons.PLUS_CIRCLE,
      items: addToShipmentCommands.value,
      visible: !!addToShipmentCommands.value.length,
    },
    {
      label: t('common.removeFromParam', {
        param: t('shipments.shipments'),
      }),
      icon: PrimeIcons.MINUS_CIRCLE,
      severity: Severity.DANGER,
      items: removeFromShipmentCommands.value,
      visible: !!removeFromShipmentCommands.value.length,
    },
    {
      label: t('routeBuilder.removeFromAllShipments'),
      icon: PrimeIcons.MINUS_CIRCLE,
      severity: Severity.DANGER,
      visible: containingShipments.value.length > 1,
      command: () =>
        emit('remove-from-shipment', {
          shipOrder: shipOrder.value,
          shipmentIdentifiers: containingShipments.value
            .map((s) => s.identifier)
            .filter(notNull),
        }),
    },
    {
      label: t('common.removeFromParam', {
        param: t('routeBuilder.planningPool'),
      }),
      icon: PrimeIcons.MINUS,
      // TODO: ticket says show it disabled with an explanatory tooltip; requires rygen ui change
      visible: !containingShipments.value.length,
      command: () =>
        emit('remove-from-planning-pool', { shipOrders: [shipOrder.value] }),
    },
    {
      label: t('shipOrders.shipOrderSplit.split'),
      icon: 'pi pi-arrows-h',
      command: () =>
        emit('split', { shipOrder: shipOrder.value, splitLineItems: [] }),
      visible:
        shipOrder.value.planningStatus === PlanningStatus.UNPLANNED &&
        !containingShipments.value.length,
    },
  ];
});
</script>
